import { AlertTriangle, CloudOff, RefreshCw, ServerCog, ShieldAlert } from "lucide-react";
import type { ReactNode } from "react";
import { ApiError, isApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { cn } from "@/lib/utils";

/**
 * Renders a failed request honestly: permission, missing server capability ("not available on
 * the server yet"), network, or a generic failure with a retry.
 */
export function ErrorState({
  error,
  onRetry,
  title,
  className,
  compact = false,
  unavailableHint,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  className?: string;
  compact?: boolean;
  /** What this feature needs from the backend, shown when the endpoint doesn't exist yet. */
  unavailableHint?: ReactNode;
}) {
  const err = isApiError(error) ? error : new ApiError({ status: 500, code: "UNKNOWN", message: "Unexpected error" });
  const kind = err.isUnavailable ? "unavailable" : err.isForbidden ? "forbidden" : err.isNetwork ? "network" : "error";
  const Icon = { unavailable: ServerCog, forbidden: ShieldAlert, network: CloudOff, error: AlertTriangle }[kind];
  const heading =
    title ??
    {
      unavailable: "Not available on the server yet",
      forbidden: "You don't have access",
      network: "Can't reach Rogue",
      error: "Something went wrong",
    }[kind];
  const description = kind === "unavailable" ? (unavailableHint ?? "The connected Rogue backend doesn't provide this data yet.") : err.userMessage;

  if (compact) {
    return (
      <div role="alert" className={cn("flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-muted-foreground text-xs", className)}>
        <Icon className="size-3.5 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="font-medium text-foreground">{heading}.</span> {description}
        </span>
        {onRetry && kind !== "unavailable" && kind !== "forbidden" ? (
          <Button variant="ghost" size="xs" onClick={onRetry}>
            Retry
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <Empty role="alert" className={cn("border border-dashed", className)}>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon />
        </EmptyMedia>
        <EmptyTitle>{heading}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      {onRetry && kind !== "unavailable" && kind !== "forbidden" ? (
        <EmptyContent>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw data-icon="inline-start" />
            Try again
          </Button>
        </EmptyContent>
      ) : null}
    </Empty>
  );
}
